import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  FormControl,
  Tooltip,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  Chip,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import PhoneIcon from "@mui/icons-material/Phone";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Controller } from "react-hook-form";
import axios from "axios";
import { useSelector } from "react-redux";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import { pageScrollStyle } from "../../../../styles/styles";

export const CustomTextarea = ({
  name,
  placeholder,
  maxLength,
  value,
  onChange,
  onBlur,
  required,
  disabled,
  readonly,
  rows = 2,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        "& textarea": {
          width: "100%",
          height: "2.5rem",
          padding: "8px 12px",
          borderRadius: "4px",
          fontSize: "0.875rem",
          fontFamily: "Roboto, sans-serif",
          border: "1px solid rgba(0, 0, 0, 0.23)",
          outline: "none",
          backgroundColor: disabled ? "#f5f5f5" : "#fff",
          color: disabled ? "rgba(0, 0, 0, 0.6)" : "inherit",
          boxShadow: "0px 0px 1px rgba(0,0,0,0.1)",
          transition: "border 0.3s ease",
          resize: "vertical",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
            "&:hover": {
              background: "#555",
            },
          },
        },
      }}
    >
      <textarea
        id={name}
        name={name}
        placeholder={placeholder}
        maxLength={maxLength}
        value={value || ""}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        readOnly={readonly}
        rows={rows}
      />
    </Box>
  );
};

const InputFields = ({
  field,
  onChange,
  onBlur,
  className,
  width = "100%",
  control,
  errors,
  register,
}) => {
  const listRef = useRef(null);
  const [apiOptions, setApiOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useSelector((data) => data.user);
  const [nextUrl, setNextUrl] = useState(null);

  const {
    label,
    field_type,
    name,
    value,
    placeholder,
    maxLength,
    required,
    tooltip,
    tooltipText,
    readonly,
    disabled,
    pickList = {
      options: [
        { label: "Option 1", value: "option_1" },
        { label: "Option 2", value: "option_2" },
      ],
    },
    rows,
    multiple,
    id,
  } = field;

  const hasApiUrl = pickList?.link_option?.tab_api;

  useEffect(() => {
    if (hasApiUrl) {
      const fetchOptions = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(pickList.link_option.tab_api, {
            headers: {
              Authorization: `Token ${token}`,
            },
          });
          const formattedOptions = formatApiResponse({
            res: response?.data?.results,
            tabName: pickList?.link_option?.tab,
            api: pickList?.link_option?.tab_api,
          });
          setApiOptions(formattedOptions);
          setNextUrl(response?.data?.next);
        } catch (error) {
          console.error("Error fetching picklist options:", error);
          setApiOptions([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchOptions();
    }
  }, [hasApiUrl, pickList?.link_option?.tab_api]);

  const options = hasApiUrl
    ? apiOptions
    : pickList?.options || [
        { label: "Option 1", value: "option_1" },
        { label: "Option 2", value: "option_2" },
      ];

  const formatApiResponse = (data) => {
    if (data?.api && data?.tabName === "contact") {
      if (Array.isArray(data?.res)) {
        return data?.res?.map((item) => ({
          label:
            item?.first_name || item?.last_name
              ? `${item?.first_name + " " + item?.last_name}`
              : "-",
          contact_type: item?.contact_type ?? "unknown",
          value: item?.id.toString(),
        }));
      }
    } else if (data?.api && data?.tabName === "member") {
      if (Array.isArray(data?.res)) {
        return data?.res?.map((item) => ({
          label:
            item?.first_name || item?.last_name
              ? `${item?.first_name + " " + item?.last_name}`
              : "-",
          value: item?.id.toString(),
        }));
      }
    }

    return [];
  };

  const fieldId = id;
  const isControlled = !!control;
  const isMultiSelect = multiple || pickList?.multiSelect;

  const handleChange = (e) => {
    onChange && onChange(e.target.value, name);
  };

  const handleAutocompleteChange = (_, newValue) => {
    if (isMultiSelect) {
      const values = newValue.map((item) =>
        typeof item === "object" ? item.value : item
      );
      onChange && onChange(values, name);
    } else {
      const extractedValue =
        newValue && typeof newValue === "object" ? newValue.value : newValue;
      onChange && onChange(extractedValue, name);
    }
  };

  const handleCheckboxChange = (e) => {
    onChange && onChange(e.target.checked, name);
  };

  const handleScroll = async (e) => {
    const listNode = e.currentTarget;

    if (
      !isLoading &&
      nextUrl &&
      listNode.scrollTop + listNode.clientHeight >= listNode.scrollHeight - 100
    ) {
      setIsLoading(true);
      try {
        const response = await axios.get(nextUrl, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });
        const formattedOptions = formatApiResponse({
          res: response?.data?.results,
          tabName: pickList?.link_option?.tab,
          api: nextUrl,
        });
        setApiOptions((prev) => [...prev, ...formattedOptions]);
        setNextUrl(response?.data?.next);
      } catch (error) {
        console.error("Error fetching more options:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Adornment helpers
  const getStartAdornment = () => {
    if (field_type === "email") {
      return (
        <EmailRoundedIcon
          sx={{ fontSize: "1.2rem", color: "text.secondary" }}
        />
      );
    } else if (field_type === "phone") {
      return <PhoneIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />;
    }
    return null;
  };

  const getEndAdornment = () => {
    if (field_type === "currency") {
      return (
        <AttachMoneyIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />
      );
    } else if (field_type === "percent") {
      return (
        <PercentIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />
      );
    }
    return null;
  };

  const hasStartAdornment = !!getStartAdornment();
  const hasEndAdornment = !!getEndAdornment();

  // Common props for both controlled and uncontrolled inputs
  const getCommonInputProps = () => ({
    id: fieldId,
    name: name,
    placeholder,
    required,
    disabled,
    size: "small",
    fullWidth: true,
    error: !!errors?.[fieldId],
    helperText: errors?.[fieldId]?.message,
    InputProps: {
      readOnly: readonly,
      ...(hasStartAdornment && {
        startAdornment: (
          <InputAdornment
            position="start"
            sx={{
              ml: "-0.9rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: "0.5rem",
              borderRight: "1px solid #e0e0e0",
            }}
          >
            {getStartAdornment()}
          </InputAdornment>
        ),
      }),
      ...(hasEndAdornment && {
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: "0.5rem",
              borderLeft: "1px solid #e0e0e0",
              mr: "-0.9rem",
            }}
          >
            {getEndAdornment()}
          </InputAdornment>
        ),
      }),
    },
  });

  // Render controlled component with react-hook-form
  const renderControlledField = () => {
    const commonRules = {
      required: required ? "This field is required" : false,
    };

    // Validation rules based on field type
    const getValidationRules = (fieldType) => {
      const rules = { ...commonRules };
      switch (fieldType) {
        case "email":
          rules.pattern = {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address",
          };
          break;
        case "phone":
          rules.pattern = {
            value: /^[0-9+-\s()]*$/,
            message: "Invalid phone number",
          };
          break;
        case "number":
        case "decimal":
        case "long_integer":
        case "currency":
          rules.pattern = {
            value: /^-?\d*\.?\d*$/,
            message: "Must be a valid number",
          };
          break;
        case "percent":
          rules.pattern = {
            value: /^-?\d*\.?\d*$/,
            message: "Must be a valid number",
          };
          rules.max = {
            value: 100,
            message: "Percentage cannot exceed 100%",
          };
          rules.min = {
            value: 0,
            message: "Percentage cannot be negative",
          };
          break;
      }

      return rules;
    };

    // Determine field type and component to use
    const getControlledComponent = () => {
      switch (field_type) {
        case "single_line":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue=""
              rules={commonRules}
              render={({ field: hookFormField }) => (
                <TextField
                  {...hookFormField}
                  {...getCommonInputProps()}
                  type="text"
                  inputProps={{ maxLength: field.characterLimit || maxLength }}
                  variant="outlined"
                />
              )}
            />
          );
        case "multi_line":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue=""
              rules={commonRules}
              render={({ field: hookFormField }) => (
                <CustomTextarea
                  {...hookFormField}
                  name={fieldId}
                  placeholder={placeholder}
                  maxLength={field.characterLimit || maxLength}
                  required={required}
                  disabled={disabled}
                  readonly={readonly}
                  rows={rows || 2}
                />
              )}
            />
          );
        case "email":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue=""
              rules={getValidationRules("email")}
              render={({ field: hookFormField }) => (
                <TextField
                  {...hookFormField}
                  {...getCommonInputProps()}
                  type="email"
                  inputProps={{ maxLength }}
                  variant="outlined"
                />
              )}
            />
          );
        case "phone":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue=""
              rules={getValidationRules("phone")}
              render={({ field: hookFormField }) => (
                <TextField
                  {...hookFormField}
                  {...getCommonInputProps()}
                  type="tel"
                  inputProps={{ maxLength }}
                  variant="outlined"
                />
              )}
            />
          );
        case "pick_list":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue={isMultiSelect ? [] : ""}
              rules={commonRules}
              render={({ field: hookFormField }) => (
                <Autocomplete
                  {...hookFormField}
                  multiple={isMultiSelect}
                  options={options}
                  loading={isLoading}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option.label || "";
                  }}
                  isOptionEqualToValue={(option, val) => {
                    if (typeof option === "object" && typeof val === "object") {
                      return option.value === val.value;
                    }
                    if (typeof option === "object" && typeof val === "string") {
                      return option.value === val;
                    }
                    return option === val;
                  }}
                  ListboxProps={{
                    onScroll: handleScroll,
                    ref: listRef,
                    style: { maxHeight: "300px" },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={placeholder}
                      size="small"
                      required={required}
                      error={!!errors?.[fieldId]}
                      helperText={errors?.[fieldId]?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option, state) =>
                    option?.contact_type ? (
                      renderContactOption(props, option)
                    ) : (
                      <li {...props} key={option.value}>
                        {option?.label}
                      </li>
                    )
                  }
                  renderTags={(tagValue, getTagProps) =>
                    tagValue?.map((option, index) => (
                      <Chip
                        label={
                          typeof option === "object" ? option.label : option
                        }
                        size="small"
                        {...getTagProps({ index })}
                        key={`chip-${index}`}
                      />
                    ))
                  }
                  onChange={(_, newValue) => {
                    if (isMultiSelect) {
                      const values = newValue.map((item) =>
                        typeof item === "object" ? item.value : item
                      );
                      hookFormField.onChange(values);
                    } else {
                      const value = newValue
                        ? typeof newValue === "object"
                          ? newValue.value
                          : newValue
                        : "";
                      hookFormField.onChange(value);
                    }

                    handleAutocompleteChange(_, newValue);
                  }}
                  value={(() => {
                    if (isMultiSelect) {
                      if (
                        !hookFormField.value ||
                        !Array.isArray(hookFormField.value)
                      )
                        return [];
                      return hookFormField.value.map((val) => {
                        const matchingOption = options.find(
                          (opt) => opt.value === val
                        );
                        return matchingOption || val;
                      });
                    } else {
                      if (!hookFormField.value) return null;
                      const matchingOption = options.find(
                        (opt) => opt.value === hookFormField.value
                      );
                      return matchingOption || hookFormField.value;
                    }
                  })()}
                />
              )}
            />
          );
        case "date":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue={null}
              rules={commonRules}
              render={({ field: hookFormField }) => (
                <DatePicker
                  {...hookFormField}
                  value={hookFormField.value || null}
                  onChange={(date) => hookFormField.onChange(date)}
                  disabled={disabled}
                  readOnly={readonly}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: required,
                      error: !!errors?.[fieldId],
                      helperText: errors?.[fieldId]?.message,
                      InputProps: {
                        className:
                          !hookFormField.value && !errors?.[fieldId]
                            ? "MuiOutlinedInput-notchedOutline"
                            : "",
                      },
                    },
                    popper: {
                      sx: {
                        zIndex: 1600,
                      },
                    },
                  }}
                  format="MM/DD/YYYY"
                />
              )}
            />
          );
        case "date_time":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue={null}
              rules={commonRules}
              render={({ field: hookFormField }) => (
                <DateTimePicker
                  {...hookFormField}
                  value={hookFormField.value || null}
                  onChange={(date) => hookFormField.onChange(date)}
                  disabled={disabled}
                  readOnly={readonly}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: required,
                      error: !!errors?.[fieldId],
                      helperText: errors?.[fieldId]?.message,
                      InputProps: {
                        className:
                          !hookFormField.value && !errors?.[fieldId]
                            ? "MuiOutlinedInput-notchedOutline"
                            : "",
                      },
                    },
                    popper: {
                      sx: {
                        zIndex: 1600,
                      },
                    },
                  }}
                  format="MM/DD/YYYY hh:mm A"
                />
              )}
            />
          );
        case "number":
        case "decimal":
        case "long_integer":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue=""
              rules={getValidationRules("number")}
              render={({ field: hookFormField }) => (
                <TextField
                  {...hookFormField}
                  {...getCommonInputProps()}
                  type="number"
                  inputProps={{
                    maxLength,
                    step: value === "decimal" ? "0.01" : "1",
                  }}
                  variant="outlined"
                />
              )}
            />
          );
        case "currency":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue=""
              rules={getValidationRules("currency")}
              render={({ field: hookFormField }) => (
                <TextField
                  {...hookFormField}
                  {...getCommonInputProps()}
                  type="number"
                  inputProps={{
                    maxLength,
                    step: "0.01",
                  }}
                  variant="outlined"
                />
              )}
            />
          );
        case "percent":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue=""
              rules={getValidationRules("percent")}
              render={({ field: hookFormField }) => (
                <TextField
                  {...hookFormField}
                  {...getCommonInputProps()}
                  type="number"
                  inputProps={{
                    maxLength,
                    step: "0.01",
                    min: "0",
                    max: "100",
                  }}
                  variant="outlined"
                />
              )}
            />
          );
        case "check_box":
          return (
            <Controller
              name={name}
              control={control}
              defaultValue={false}
              rules={commonRules}
              render={({ field: hookFormField }) => (
                <FormControl
                  fullWidth
                  error={!!errors?.[fieldId]}
                  sx={{
                    border: "1px solid #ddd",
                    mt: "0.45rem",
                    borderRadius: "0.2rem",
                    pl: "1rem",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...hookFormField}
                        checked={!!hookFormField.value}
                        disabled={disabled}
                        readOnly={readonly}
                        size="small"
                      />
                    }
                    label={placeholder || label || ""}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.8rem",
                        fontWeight: 400,
                        color: disabled ? "text.disabled" : "text.primary",
                      },
                    }}
                  />
                  {errors?.[fieldId] && (
                    <Typography color="error" variant="caption">
                      {errors[fieldId].message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          );
        default:
          return (
            <Controller
              name={name}
              control={control}
              defaultValue=""
              rules={commonRules}
              render={({ field: hookFormField }) => (
                <TextField
                  {...hookFormField}
                  {...getCommonInputProps()}
                  type={field_type || "text"}
                  inputProps={{ maxLength }}
                  variant="outlined"
                />
              )}
            />
          );
      }
    };

    return getControlledComponent();
  };

  // Render uncontrolled component
  const renderUncontrolledField = () => {
    switch (field_type) {
      case "single_line":
        return (
          <TextField
            {...getCommonInputProps()}
            type="text"
            inputProps={{ maxLength }}
            value={value || ""}
            onChange={handleChange}
            onBlur={onBlur}
            variant="outlined"
          />
        );
      case "multi_line":
        return (
          <CustomTextarea
            name={name}
            placeholder={placeholder}
            maxLength={maxLength}
            value={value || ""}
            onChange={handleChange}
            onBlur={onBlur}
            required={required}
            disabled={disabled}
            readonly={readonly}
            rows={rows || 2}
          />
        );
      case "email":
        return (
          <TextField
            {...getCommonInputProps()}
            type="email"
            inputProps={{ maxLength }}
            value={value || ""}
            onChange={handleChange}
            onBlur={onBlur}
            variant="outlined"
          />
        );
      case "phone":
        return (
          <TextField
            {...getCommonInputProps()}
            type="tel"
            inputProps={{ maxLength }}
            value={value || ""}
            onChange={handleChange}
            onBlur={onBlur}
            variant="outlined"
          />
        );
      case "pick_list":
        return (
          <Autocomplete
            id={fieldId}
            options={options}
            value={isMultiSelect ? [] : null}
            onChange={handleAutocompleteChange}
            multiple={isMultiSelect}
            disabled={disabled}
            readOnly={readonly}
            loading={isLoading}
            PopperProps={{
              style: { zIndex: 3000 },
              modifiers: [
                {
                  name: "zIndex",
                  enabled: true,
                  phase: "write",
                  fn({ state }) {
                    state.styles.popper.zIndex = 1600;
                  },
                },
              ],
            }}
            ListboxProps={{
              onScroll: handleScroll,
              ref: listRef,
              style: { maxHeight: "300px" },
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option.label || "";
            }}
            isOptionEqualToValue={(option, val) => {
              if (typeof option === "object" && typeof val === "object") {
                return option.value === val.value;
              }
              if (typeof option === "object" && typeof val === "string") {
                return option.value === val;
              }
              return option === val;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                size="small"
                required={required}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option, state) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={typeof option === "object" ? option.label : option}
                  size="small"
                  {...getTagProps({ index })}
                  key={`chip-${index}`}
                />
              ))
            }
          />
        );
      case "date":
        return (
          <DatePicker
            value={value || null}
            onChange={(date) => onChange && onChange(date, name)}
            disabled={disabled}
            readOnly={readonly}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                required: required,
              },
              popper: {
                sx: {
                  zIndex: 1600,
                },
              },
            }}
          />
        );
      case "date_time":
        return (
          <DateTimePicker
            value={value || null}
            onChange={(date) => onChange && onChange(date, name)}
            disabled={disabled}
            readOnly={readonly}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                required: required,
              },
              popper: {
                sx: {
                  zIndex: 1600,
                },
              },
            }}
          />
        );
      case "number":
      case "decimal":
      case "long_integer":
        return (
          <TextField
            {...getCommonInputProps()}
            type="number"
            inputProps={{
              maxLength,
              step: value === "decimal" ? "0.01" : "1",
            }}
            value={value || ""}
            onChange={handleChange}
            onBlur={onBlur}
            variant="outlined"
          />
        );
      case "currency":
        return (
          <TextField
            {...getCommonInputProps()}
            type="number"
            inputProps={{
              maxLength,
              step: "0.01",
            }}
            value={value || ""}
            onChange={handleChange}
            onBlur={onBlur}
            variant="outlined"
          />
        );
      case "percent":
        return (
          <TextField
            {...getCommonInputProps()}
            type="number"
            inputProps={{
              maxLength,
              step: "0.01",
              min: "0",
              max: "100",
            }}
            value={value || ""}
            onChange={handleChange}
            onBlur={onBlur}
            variant="outlined"
          />
        );
      case "check_box":
        return (
          <FormControl
            fullWidth
            sx={{
              border: "1px solid #ddd",
              mt: "0.45rem",
              borderRadius: "0.2rem",
              pl: "1rem",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  id={fieldId}
                  name={name}
                  checked={value === true || value === "true"}
                  onChange={handleCheckboxChange}
                  onBlur={onBlur}
                  disabled={disabled}
                  readOnly={readonly}
                  size="small"
                />
              }
              label={placeholder || label || ""}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.8rem",
                  fontWeight: 400,
                  color: disabled ? "text.disabled" : "text.primary",
                },
              }}
            />
          </FormControl>
        );
      default:
        return (
          <TextField
            {...getCommonInputProps()}
            type={field_type || "text"}
            inputProps={{ maxLength }}
            value={value || ""}
            onChange={handleChange}
            onBlur={onBlur}
            variant="outlined"
          />
        );
    }
  };

  // Special handling for checkbox field
  if (field_type === "check_box") {
    return (
      <Box className={className} sx={{ width, mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {isControlled ? renderControlledField() : renderUncontrolledField()}
          {tooltip && (
            <Tooltip title={tooltipText || ""} arrow placement="top">
              <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                <HelpRoundedIcon sx={{ fontSize: "1rem" }} color="primary" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  }
  if (!field) return null;

  // Standard field with label
  return (
    <Box className={className} sx={{ width }}>
      <FormControl fullWidth variant="outlined" size="medium">
        {label && (
          <Box sx={{ display: "flex", alignItems: "center", mb: "0.2rem" }}>
            <Typography
              variant="body1"
              component="label"
              htmlFor={fieldId}
              sx={{ fontSize: "0.8rem", fontWeight: 500 }}
            >
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </Typography>

            {tooltip && (
              <Tooltip title={tooltipText || ""} arrow placement="top">
                <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                  <HelpRoundedIcon sx={{ fontSize: "1rem" }} color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
          {isControlled ? renderControlledField() : renderUncontrolledField()}

          {required && (
            <Box
              sx={{
                position: "absolute",
                top: "0rem",
                right: "0rem",
                width: "0",
                height: "0",
                borderRight: "12px solid #dc3545",
                borderBottom: "12px solid transparent",
                zIndex: 1,
              }}
            />
          )}
        </Box>
      </FormControl>
    </Box>
  );
};

export default InputFields;

const renderContactOption = (props, option) => (
  <li
    {...props}
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography variant="body1">{option?.label ?? ""}</Typography>
    <Typography variant="body1">
      {option?.contact_type === "person" ? (
        <AccountCircleRoundedIcon
          sx={{ color: "#bf3fe1", fontSize: "0.9rem" }}
        />
      ) : (
        <BusinessRoundedIcon sx={{ color: "#2e77f5", fontSize: "0.9rem" }} />
      )}
    </Typography>
  </li>
);
